import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { eq } from 'ember-truth-helpers';
import HotelRatesTablesCellsTdBase from 'lumen/components/hotel/rates/tables/cells/td-base';
import HotelRatesTooltipsShortTermRentalTooltip from 'lumen/components/hotel/rates/tooltips/short-term-rental-tooltip';
import changeColorClass from 'lumen/helpers/change-color-class';
import { COMPARETYPES } from 'lumen/utils/constants/general-constants';
import { isComparingRates } from 'lumen/utils/is-comparing-rates';

export default class HotelRatesTablesCellsTdShortTermRentalRate extends HotelRatesTablesCellsTdBase {
  @service declare router: Services['router'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get differenceForCompare() {
    if (!this.compareValue) {
      return null;
    }
    return this.period.rentalRateForCurrentDay?.differenceForExtractDate(
      this.compareValue,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDifferenceForCompare() {
    if (!this.compareValue) {
      return null;
    }
    return this.period.rentalRateForCurrentDay?.formattedDifferenceForExtractDate(
      this.compareValue,
      { includeCurrency: false },
    );
  }

  get layoutConditionalClassNames(): string {
    const { layout, textAlign } = this.args.column;
    const justify = textAlign === 'left' ? 'justify-start' : 'justify-end';
    const isRowLayout = layout === 'side-by-side';

    return isRowLayout
      ? `td-short-term-rental-rate--row ${justify}`
      : 'td-short-term-rental-rate--column';
  }

  get showAlerts(): boolean {
    return (
      !isComparingRates(this.compareType, this.compareValue) &&
      this.period.hasRateShortTermRentalAlert
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showChangeColor() {
    return (
      this.period.ownHotelRate.value &&
      this.period.rentalRateForCurrentDay?.value &&
      !this.showRateChanges
    );
  }

  get showRateChanges(): boolean {
    const isDateCompare = this.compareType === COMPARETYPES.DATE;
    return isDateCompare && !this.isPast;
  }

  @action
  redirectToShortTermRental(): void {
    const currentDay = this.period?.day?.date?.format('YYYY-MM-DD');
    this.router.transitionTo('hotel.short-term-rental', {
      queryParams: { fromDate: currentDay, toDate: currentDay },
    });
  }

  <template>
    <div
      class="rental-rate-wrapper tabular-nums
        {{if
          (eq @column.textAlign 'left')
          'flex items-center justify-start'
          'flex items-center justify-end'
        }}"
    >
      {{#if this.showAlerts}}
        <div class="alert-rate-change mr-100 flex items-center">
          <button
            class="button--unstyled is-link mr-100"
            type="button"
            {{on "click" this.redirectToShortTermRental}}
          >
            <PrismIcon @name="short-term-rental" @size="extra-small" />
          </button>
          <span class="alert-indicator"></span>
          <EmberTooltip
            @delay={{25}}
            @side="bottom"
            @tooltipClass="base-tooltip tooltip-short-term-rental"
          >
            <HotelRatesTooltipsShortTermRentalTooltip
              @hasOTBShortTermRentalAlert={{false}}
              @hasRateShortTermRentalAlert={{true}}
              @hasShortTermRentalAlert={{true}}
              @period={{this.period}}
              @showOnlyAlert={{true}}
            />
          </EmberTooltip>
        </div>
      {{/if}}

      <div
        class="text-200--regular mr-500
          {{this.layoutConditionalClassNames}}
          {{if this.isPast 'td-past'}}
          {{if this.isToday 'td-today'}}"
      >
        <div
          class="truncate
            {{if this.showRateChanges 'flex items-center justify-center'}}"
        >
          <p
            class="truncate
              {{if
                this.showChangeColor
                (changeColorClass
                  this.period.rentalRateForCurrentDay.value
                  reference=this.period.ownHotelRate.value
                )
              }}"
          >
            {{this.period.rentalFormmattedRateValue}}
          </p>
        </div>
        {{#if this.showRateChanges}}
          <div
            class="td-right-value
              {{changeColorClass this.differenceForCompare}}"
          >
            {{this.formattedDifferenceForCompare}}
          </div>
        {{else if this.compareType}}
          &nbsp;
        {{/if}}
      </div>
    </div>
  </template>
}
